<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { required } from "vuelidate/lib/validators";
import Quote from "@/services/westore/entities/quote";
import CommonEndPoints from "@/services/westore/entities/common";
import Faqs from "../../../components/faqs.vue";
import FormSkeleton from "../../../components/skeletons/form-skeleton.vue";
import { mapGetters } from "vuex";

export default {
  page: {
    title: "Quote Request",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    Faqs,
    FormSkeleton,
  },
  data() {
    return {
      title: this.$t('Quote Request'),
      subtitle: this.$t(
        "Information below will help us understand your business more."
      ),
      submitted: false,
      loader: false,
      showContentLoader: true,
      isSuccess: false,
      successMessage: "",
      faqs: [
        { question: "Question 1", answer: "Answer Answer" },
        {
          question: "How long would it take for the order to be executed?",
          answer:
            "To enable automatic updates for Admin Columns Pro, enter your license key. If you don't have a license key, please see details & pricing.",
        },
      ],
      formInputs: {
        iNeed: [],
        productTypes: [],
        shipmentIn: null,
        isInsured: 0,
        warehouseLocation: null,
        temperatureStorage: null,
        packedIn: null,
        inboundShipment: null,
      },
      needTypes: [],
      productTypes: [],
      shipmentInTypes: [],
      locations: [],
      temperatureStorageTypes: [],
      packedInTypes: [],
      inboundShipmentTypes: [],
      expandForm: false,
    };
  },
  validations: {
    formInputs: {
      shipmentIn: {
        required,
      },
      isInsured: {
        required,
      },
      warehouseLocation: {
        required,
      },
      temperatureStorage: {
        required,
      },
      packedIn: {
        required,
      },
      inboundShipment: {
        required,
      },
    },
  },
  created() {
    this.fetchOptions().then(() => this.init());
  },
  computed: {
    ...mapGetters({
      userCountry: ["auth/userCountry"],
      services: ["auth/services"],
    }),
    checkLoader() {
      return this.loader;
    },
  },
  methods: {
    init() {
      this.formInputs.productTypes = [];
      this.formInputs.iNeed = this.needTypes[0].id;
      this.formInputs.shipmentIn = this.shipmentInTypes[0].value;
      this.formInputs.warehouseLocation = this.locations[0].id;
      this.formInputs.temperatureStorage =
        this.temperatureStorageTypes[0].value;
      this.formInputs.packedIn = this.packedInTypes[0].value;
      this.formInputs.inboundShipment = this.inboundShipmentTypes[0].value;
      this.showContentLoader = false;
    },
    submitForm() {
      this.$v.$touch();
      if (!this.$v.$invalid && this.formInputs.productTypes.length > 0) {
        this.loader = true;

        const payloadRequest = {
          services: [this.formInputs.iNeed],
          product_types: this.formInputs.productTypes,
          city_id: this.formInputs.warehouseLocation,
          shipment_type: this.formInputs.shipmentIn,
          product_insured: this.formInputs.isInsured,
          temperature: this.formInputs.temperatureStorage,
          packaging_type: this.formInputs.packedIn,
          shipment_handling: this.formInputs.inboundShipment,
        };

        Quote.store(payloadRequest)
          .then((res) => {
            this.successMessage = "Quote Request has been created successfully";
            this.isSuccess = true;
            setTimeout(
              () =>
                this.$router.push({
                  name: "quote-details",
                  params: { id: res.data.data.id },
                }),
              3000
            );
          })
          .catch((error) => {
            console.log("error", error.response);
          });
      }
    },

    async fetchOptions() {
      const { data: options } = await Quote.getOptions();

      this.needTypes = options.services;
      this.productTypes = options.product_types;
      this.shipmentInTypes = options.shipment_types;
      this.temperatureStorageTypes = options.temperatures;
      this.packedInTypes = options.packaging_types;
      this.inboundShipmentTypes = options.shipment_handling;

      const { data: cities } = await CommonEndPoints.getCities(
        this.userCountry.id
      );
      this.locations = cities;

      return Promise.resolve();
    },
    toggleFormListener(val) {
      this.expandForm = val;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :subtitle="subtitle" />
    <div class="row">
      <div
        class="col-md-9 col-sm-12 order-2 order-sm-12"
        :class="expandForm && 'col-md-11 col-sm-12'"
      >
        <div class="card">
          <FormSkeleton v-if="showContentLoader" />
          <div class="card-body" v-else>
            <div class="row">
              <div class="col-12">
                <form class="form-horizontal" @submit.prevent="submitForm">
                  <!-- I Need -->
                  <div class="mb-3">
                    <label class="col-form-label"
                      >{{ $t("I Need") }}
                      <code class="pink--text">*</code></label
                    >
                    <select
                      class="form-control"
                      v-model="formInputs.iNeed"
                      :class="{
                        'is-invalid': submitted && $v.formInputs.iNeed.$error,
                      }"
                    >
                      <option
                        v-for="item in needTypes"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{ item.name }}
                      </option>
                    </select>
                    <div
                      v-if="submitted && !$v.formInputs.iNeed.required"
                      class="invalid-feedback"
                    >
                      iNeed is required.
                    </div>
                  </div>
                  <!-- Product Type -->
                  <div class="mb-3 col-12">
                    <label class="row col-form-label"
                      >{{ $t("Product Type") }}
                      <div
                        v-if="submitted && formInputs.productTypes.length === 0"
                      >
                        <code class="pink--text"
                          >Product Type is required.</code
                        >
                      </div>
                    </label>
                    <div class="row pt-1">
                      <div
                        class="form-check form-check-info mb-0 col-md-6"
                        style="padding-bottom: 0; padding-top: 0"
                        v-for="product in productTypes"
                        :key="product.id"
                      >
                        <input
                          class="form-check-input"
                          type="checkbox"
                          v-model="formInputs.productTypes"
                          :value="product.id"
                        />
                        <label class="form-check-label">{{
                          product.name
                        }}</label>
                      </div>
                    </div>
                  </div>
                  <!-- Shipment is coming in -->
                  <div class="mb-3">
                    <label class="col-form-label">{{
                      $t("Shipment is coming in")
                    }}</label>
                    <select
                      class="form-control"
                      v-model="formInputs.shipmentIn"
                      :class="{
                        'is-invalid':
                          submitted && $v.formInputs.shipmentIn.$error,
                      }"
                    >
                      <option
                        v-for="item in shipmentInTypes"
                        :key="item.value"
                        :value="item.value"
                      >
                        {{ item.text }}
                      </option>
                    </select>
                    <div
                      v-if="submitted && !$v.formInputs.shipmentIn.required"
                      class="invalid-feedback"
                    >
                      Shipment is required.
                    </div>
                  </div>
                  <!-- Are your items insured? -->
                  <div class="mb-3 row">
                    <label class="col-lg-2 col-sm-12 col-form-label">{{
                      $t("Are your items insured?")
                    }}</label>
                    <b-form-group class="col-lg-10 col-sm-12">
                      <b-form-radio
                        v-model="formInputs.isInsured"
                        :class="isRtl ? 'mb-2 float-right':'mb-2 float-left'"
                        value="1"
                        plain
                        >{{ $t("Yes") }}</b-form-radio
                      >
                      <b-form-radio
                        v-model="formInputs.isInsured"
                        :class="isRtl ? 'mb-2 mr-3  float-right':'mb-2 ml-3  float-left'"
                        value="0"
                        plain
                        >{{ $t("No") }}</b-form-radio
                      >
                    </b-form-group>
                  </div>
                  <!-- Warehouse Location -->
                  <div class="mb-3">
                    <label class="col-form-label"
                      >{{ $t("Warehouse Location") }}</label
                    >
                    <select
                      class="form-control"
                      v-model="formInputs.warehouseLocation"
                      :class="{
                        'is-invalid':
                          submitted && $v.formInputs.warehouseLocation.$error,
                      }"
                    >
                      <option
                        v-for="item in locations"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{ item.name }}
                      </option>
                    </select>
                    <div
                      v-if="
                        submitted && !$v.formInputs.warehouseLocation.required
                      "
                      class="invalid-feedback"
                    >
                      Warehouse Location is required.
                    </div>
                  </div>
                  <!-- Storage -->
                  <div class="mb-3">
                    <label class="col-form-label"
                      >
                      {{$t('I need my products to be stored in temperature-controlled storage*')}}
                      <code class="pink--text">*</code></label
                    >
                    <select
                      class="form-control"
                      v-model="formInputs.temperatureStorage"
                      :class="{
                        'is-invalid':
                          submitted && $v.formInputs.temperatureStorage.$error,
                      }"
                    >
                      <option
                        v-for="item in temperatureStorageTypes"
                        :key="item.value"
                        :value="item.value"
                      >
                        {{ item.text }}
                      </option>
                    </select>
                    <div
                      v-if="
                        submitted && !$v.formInputs.temperatureStorage.required
                      "
                      class="invalid-feedback"
                    >
                      temperature-controlled storage is required.
                    </div>
                  </div>
                  <!-- I want my product to be packed -->
                  <div class="mb-3">
                    <label class="col-form-label">
                      {{ $t("I want my product to be packed") }}
                      <code class="pink--text">*</code></label
                    >
                    <select
                      class="form-control"
                      v-model="formInputs.packedIn"
                      :class="{
                        'is-invalid':
                          submitted && $v.formInputs.packedIn.$error,
                      }"
                    >
                      <option
                        v-for="item in packedInTypes"
                        :key="item.value"
                        :value="item.value"
                      >
                        {{ item.text }}
                      </option>
                    </select>
                    <div
                      v-if="submitted && !$v.formInputs.packedIn.required"
                      class="invalid-feedback"
                    >
                      Packed is required.
                    </div>
                  </div>
                  <!-- Inbound Shipments -->
                  <div class="mb-3">
                    <label class="col-form-label">
                      {{ $t("Inbound Shipments") }}
                      <code class="pink--text">*</code></label
                    >
                    <select
                      class="form-control"
                      v-model="formInputs.inboundShipment"
                      :class="{
                        'is-invalid':
                          submitted && $v.formInputs.inboundShipment.$error,
                      }"
                    >
                      <option
                        v-for="item in inboundShipmentTypes"
                        :key="item.value"
                        :value="item.value"
                      >
                        {{ item.text }}
                      </option>
                    </select>
                    <div
                      v-if="
                        submitted && !$v.formInputs.inboundShipment.required
                      "
                      class="invalid-feedback"
                    >
                      Inbound Shipments is required.
                    </div>
                  </div>
                  <!-- Submit -->
                  <div class="row float-end">
                    <div class="col-md-2">
                      <v-progress-circular
                        v-if="checkLoader"
                        :size="30"
                        color="secondary"
                        indeterminate
                      ></v-progress-circular>
                      <b-button
                        v-else
                        type="submit"
                        variant="primary"
                        class="btn-block"
                      >
                        {{ $t("Submit") }}</b-button
                      >
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <!-- end row -->
          </div>
        </div>
        <!-- end card -->
      </div>
      <div
        class="order-1 order-sm-12"
        :class="
          expandForm
            ? 'col-md-1 col-sm-12  d-flex justify-content-end'
            : 'col-md-3 col-sm-12'
        "
      >
        <Faqs :faqs="faqs" title="Quotes" @toggleForm="toggleFormListener">
          <v-expansion-panels>
            <v-expansion-panel class="panel">
              <v-expansion-panel-header class="panel__header">
                Question 1
                <template v-slot:actions>
                  <v-icon color="pink"> $expand </v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="panel__content opacity-75">
                Answer Answer
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel class="panel">
              <v-expansion-panel-header class="panel__header">
                How long would it take for the order to be executed?
                <template v-slot:actions>
                  <v-icon color="pink"> $expand </v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="panel__content opacity-75">
                To enable automatic updates for Admin Columns Pro, enter your
                license key. If you don't have a license key, please see details
                & pricing.
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </Faqs>
      </div>
      <!-- end col -->
    </div>

    <v-snackbar
      color="success accent-2"
      elavation="24"
      top
      right
      v-model="isSuccess"
    >
      {{ successMessage }}
    </v-snackbar>
  </Layout>
</template>
